import { Component, ViewEncapsulation } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { HttpParams } from '@angular/common/http'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCircleCheck, faTriangleExclamation, faGasPump, faOilCan, faWrench, faTruck } from '@fortawesome/free-solid-svg-icons'

import { EquipmentReport, EquipmentReportType, EquipmentReportTypeEx, ListItem, RepositoryModal } from '../../shared'
import { IssueReport } from './report-components/issue-report.model'
import { IssueReportFormComponent } from './report-components/issue-report-form/issue-report-form.component'
import { FuelRequest } from './report-components/fuel-request.model'
import { FuelRequestFormComponent } from './report-components/fuel-request-form/fuel-request-form.component'
import { DailyReport, InspectionItem, InspectionStatus } from './report-components/daily-report.model'
import { DailyReportFormComponent } from './report-components/daily-report-form/daily-report-form.component'
import { GreaseFluidRequest } from './report-components/grease-fluid-request.model'
import { GreaseFluidRequestFormComponent } from './report-components/grease-fluid-request-form/grease-fluid-request-form.component'
import { MechanicInspection } from './report-components/mechanic-inspection.model'
import { MechanicInspectionFormComponent } from './report-components/mechanic-inspection-form/mechanic-inspection-form.component'
import { TireReport } from './report-components/tire-report.model'
import { TireReportFormComponent } from './report-components/tire-report-form/tire-report-form.component'
import { EquipmentReportComponent } from './equipment-report.component'

interface EquipmentSkillCategoryDto {
    code: string
    name: string
    sortOrder: number
}

interface MechanicDto {
    hrRef: number
    displayName: string
}

@Component({
    templateUrl: './equipment-report-modal.component.html',
    styleUrls: ['./equipment-report-modal.component.css'],
    standalone: true,
    imports: [FormsModule, FaIconComponent, IssueReportFormComponent, FuelRequestFormComponent, GreaseFluidRequestFormComponent, DailyReportFormComponent, MechanicInspectionFormComponent, TireReportFormComponent],
    encapsulation: ViewEncapsulation.None,
})
export class EquipmentReportModalComponent extends RepositoryModal<EquipmentReport> {
    step = 0
    selectedReportType: EquipmentReportType | null = null
    EquipmentReportType = EquipmentReportType
    issueReport = new IssueReport()
    fuelRequest = new FuelRequest()
    dailyReport = new DailyReport()
    greaseFluidRequest = new GreaseFluidRequest()
    mechanicInspection = new MechanicInspection()
    tireReport = new TireReport()
    skillCategoryOptions: ListItem<string>[] = [new ListItem('', 'Select...')]
    mechanicOptions: ListItem<number>[] = [new ListItem<number>(0, 'No suggestion')]
    private parentComponent!: EquipmentReportComponent

    get jobOptions(): ListItem<string>[] {
        return this.parentComponent?.jobMap() ?? []
    }

    get employeeOptions(): ListItem<number>[] {
        return this.parentComponent?.employeeMap() ?? []
    }

    get equipmentOptions(): ListItem<string>[] {
        return this.parentComponent?.equipmentMap() ?? []
    }

    protected override onError(errorResult: any) {
        if (typeof errorResult === 'string') {
            return
        }
        super.onError(errorResult)
    }

    faCircleCheck = faCircleCheck
    faTriangleExclamation = faTriangleExclamation
    faGasPump = faGasPump
    faOilCan = faOilCan
    faWrench = faWrench
    faTruck = faTruck

    get wizardTitle(): string {
        if (this.step === 0) {
            return 'What would you like to report?'
        }
        return this.selectedReportType != null ? EquipmentReportTypeEx.toDisplay(this.selectedReportType) : 'Equipment Report'
    }

    isLoadingDetail = false

    protected onInitialized() {
        this.parentComponent = this.editOptions.initializeParams?.component as EquipmentReportComponent
        this.loadSkillCategories()
        this.loadMechanics()

        if (this.isEditing && this.target.reportType != null) {
            this.selectedReportType = this.target.reportType
            this.step = 1
            this.loadFullReport(this.target.id, this.target.reportType)
        } else {
            this.setDefaultOperator()
        }
    }

    private loadSkillCategories() {
        const params = new HttpParams().set('activeOnly', 'true')
        this.services.data.get<EquipmentSkillCategoryDto[]>('api/EquipmentSkillCategory', params).subscribe({
            next: categories => {
                this.skillCategoryOptions = [
                    new ListItem('', 'Select...'),
                    ...categories.map(c => new ListItem(c.code, c.name)),
                ]
            },
            error: () => {
                this.services.notification.error('Failed to load system affected options.')
            },
        })
    }

    private loadMechanics() {
        this.services.data.get<MechanicDto[]>('api/EquipmentReport/mechanics').subscribe({
            next: mechanics => {
                this.mechanicOptions = [
                    new ListItem<number>(0, 'No suggestion'),
                    ...mechanics.map(m => new ListItem<number>(m.hrRef, m.displayName)),
                ]
            },
            error: () => {
                this.services.notification.error('Failed to load mechanics.')
            },
        })
    }

    private setDefaultOperator() {
        const hrref = this.services.authentication.hrref
        if (hrref) {
            this.fuelRequest.operatorName = hrref
            this.issueReport.operatorName = hrref
            this.dailyReport.operatorName = hrref
            this.greaseFluidRequest.operatorName = hrref
            this.mechanicInspection.mechanic = hrref
            this.tireReport.operatorName = hrref
        }
    }

    selectReportType(type: EquipmentReportType) {
        this.selectedReportType = type
        this.target.reportType = type
        this.step = 1
        if (type === EquipmentReportType.Tire && !this.isEditing) {
            this.setDefaultOperator()
        }
    }

    private loadFullReport(id: string, reportType: EquipmentReportType) {
        this.isLoadingDetail = true
        const params = new HttpParams().set('reportType', reportType.toString())
        this.services.data.http.get<any>(`api/EquipmentReport/${id}`, { params }).subscribe({
            next: dto => {
                this.populateFormFromDto(reportType, dto)
                this.isLoadingDetail = false
            },
            error: () => {
                this.isLoadingDetail = false
            },
        })
    }

    private populateFormFromDto(reportType: EquipmentReportType, dto: any) {
        switch (reportType) {
            case EquipmentReportType.Daily:
                this.populateDaily(dto)
                break
            case EquipmentReportType.Issue:
                this.populateIssue(dto)
                break
            case EquipmentReportType.Fuel:
                this.populateFuel(dto)
                break
            case EquipmentReportType.Grease:
                this.populateGrease(dto)
                break
            case EquipmentReportType.Mechanic:
                this.populateMechanic(dto)
                break
            case EquipmentReportType.Tire:
                this.populateTire(dto)
                break
        }
    }

    private populateDaily(dto: any) {
        const d = this.dailyReport
        d.id = dto.id
        d.dateTime = dto.dateTime ? new Date(dto.dateTime) : null
        d.jobName = dto.job ?? ''
        d.jobNumber = dto.jobNumber ?? ''
        d.operatorName = dto.operatorHrRef ?? 0
        d.equipmentId = dto.equipment ?? ''
        d.issueDetails = dto.issueDetails ?? ''
        d.equipmentStatus = dto.equipmentStatus ?? ''

        if (dto.inspections?.length) {
            const applyStatuses = (items: InspectionItem[], category: number) => {
                const fromDto = (dto.inspections as any[]).filter((i: any) => i.category === category)
                for (const item of items) {
                    const match = fromDto.find((i: any) => i.label === item.label)
                    if (match) {
                        item.status = (match.status ?? '') as InspectionStatus
                    }
                }
            }
            applyStatuses(d.preStartWalkAround, 0)
            applyStatuses(d.safetyEquipment, 1)
            applyStatuses(d.engineOperation, 2)
            applyStatuses(d.hydraulicsAttachments, 3)
            applyStatuses(d.workAreaFinalCheck, 4)
        }
    }

    private populateIssue(dto: any) {
        const r = this.issueReport
        r.id = dto.id
        r.equipmentId = dto.equipment ?? ''
        r.dateTimeOfIssue = dto.dateTime ? new Date(dto.dateTime) : null
        r.operatorName = dto.operatorHrRef ?? 0
        r.job = dto.job ?? ''
        r.hoursMileage = dto.hoursMileage ?? ''
        r.systemAffected = dto.systemAffected ?? ''
        r.failureTypes = dto.failureTypesList ?? []
        r.failureTypeOther = dto.failureTypeOther ?? ''
        r.conditionNow = dto.conditionNow ?? ''
        r.safetyStatus = dto.safetyStatus ?? ''
        r.quickObservations = dto.quickObservationsList ?? []
        r.actionsTaken = dto.actionsTakenList ?? []
        r.severity = dto.severity ?? ''
        r.shortDescription = dto.shortDescription ?? ''
        r.suggestedMechanicHrRef = dto.suggestedMechanicHrRef ?? null
        r.existingImageUrls = dto.imageUrls ?? []
    }

    private populateFuel(dto: any) {
        const f = this.fuelRequest
        f.id = dto.id
        f.dateTime = dto.dateTime ? new Date(dto.dateTime) : null
        f.job = dto.job ?? ''
        f.operatorName = dto.operatorHrRef ?? 0
        f.equipmentNumber = dto.equipment ?? ''
        f.currentFuelLevel = dto.currentFuelLevel ?? ''
        f.fuelTypeRequired = dto.fuelTypeRequired ?? ''
        f.isEquipmentRunning = dto.isEquipmentRunning ?? ''
        f.priorityLevel = dto.priorityLevel ?? ''
        f.additionalNotes = dto.additionalNotes ?? ''
    }

    private populateGrease(dto: any) {
        const g = this.greaseFluidRequest
        g.id = dto.id
        g.dateTime = dto.dateTime ? new Date(dto.dateTime) : null
        g.job = dto.job ?? ''
        g.operatorName = dto.operatorHrRef ?? 0
        g.equipmentNumber = dto.equipment ?? ''
        g.hoursMileage = dto.hoursMileage ?? ''
        g.greaseRoutineLubrication = dto.greaseRoutineLubrication ?? false
        g.greaseDryOrNoisy = dto.greaseDryOrNoisy ?? false
        g.otherFluid = dto.otherFluid ?? ''
        g.additionalNotes = dto.additionalNotes ?? ''
        if (dto.fluids?.length) {
            for (const fluid of g.fluids) {
                const match = (dto.fluids as any[]).find((f: any) => f.label === fluid.label)
                if (match) {
                    fluid.lowOrNeeded = match.lowOrNeeded ?? false
                    fluid.leakDetected = match.leakDetected ?? false
                }
            }
        }
        if (dto.conditionChecks?.length) {
            for (const cond of g.conditionChecks) {
                const match = (dto.conditionChecks as any[]).find((c: any) => c.label === cond.label)
                if (match) {
                    cond.checked = match.checked ?? false
                }
            }
        }
    }

    private populateMechanic(dto: any) {
        const m = this.mechanicInspection
        m.id = dto.id
        m.workOrderNumber = dto.workOrderNumber ?? ''
        m.equipmentId = dto.equipment ?? ''
        m.dateTime = dto.dateTime ? new Date(dto.dateTime) : null
        m.mechanic = dto.mechanicHrRef ?? 0
        m.hours = dto.hours ?? ''
        m.verifyComplaint = dto.verifyComplaint ?? ''
        m.verifyComplaintNotes = dto.verifyComplaintNotes ?? ''
        m.visualInspectionFindings = dto.visualInspectionFindingsList ?? []
        m.visualInspectionNotes = dto.visualInspectionNotes ?? ''
        m.engineSymptoms = dto.engineSymptomsList ?? []
        m.excessSmokeColor = dto.excessSmokeColor ?? ''
        m.engineBasicChecks = dto.engineBasicChecksList ?? []
        m.hydraulicSymptoms = dto.hydraulicSymptomsList ?? []
        m.hydraulicChecks = dto.hydraulicChecksList ?? []
        m.electricalChecks = dto.electricalChecksList ?? []
        m.faultCodes = dto.faultCodes ?? ''
        m.drivetrainSymptoms = dto.drivetrainSymptomsList ?? []
        m.brakeFindings = dto.brakeFindingsList ?? []
        m.coolingFindings = dto.coolingFindingsList ?? []
        m.attachmentFindings = dto.attachmentFindingsList ?? []
        m.causeIdentified = dto.causeIdentified ?? ''
        m.causeDescription = dto.causeDescription ?? ''
        m.status = dto.status ?? ''
        m.mechanicNotes = dto.mechanicNotes ?? ''
        if (dto.fluidsFilters?.length) {
            for (const fluid of m.fluidsFilters) {
                const match = (dto.fluidsFilters as any[]).find((f: any) => f.label === fluid.label)
                if (match) {
                    fluid.status = match.status ?? ''
                }
            }
        }
    }

    private populateTire(dto: any) {
        const t = this.tireReport
        t.id = dto.id
        t.equipmentId = dto.equipment ?? ''
        t.dateTimeOfIssue = dto.dateTime ? new Date(dto.dateTime) : null
        t.operatorName = dto.operatorHrRef ?? 0
        t.job = dto.job ?? ''
        t.jobNumber = dto.jobNumber ?? ''
        t.county = dto.county ?? ''
        t.equipmentClass = dto.equipmentClass || ''
        t.problemType = dto.problemType || 'Flat'
        t.axleNumber = dto.axleNumber ?? null
        t.side = dto.side || 'Left'
        t.resolvedTireSize = dto.resolvedTireSize ?? ''
        t.submitterPhone = dto.submitterPhone ?? ''
        t.locationDescription = dto.locationDescription ?? ''
        t.locationLatitude = dto.locationLatitude ?? null
        t.locationLongitude = dto.locationLongitude ?? null
        t.locationCapturedOn = dto.locationCapturedOn ? new Date(dto.locationCapturedOn) : null
        t.shortDescription = dto.shortDescription ?? ''
        t.notes = dto.notes ?? ''
        t.existingImageUrls = dto.imageUrls ?? []
        t.receiptImageUrls = dto.receiptImageUrls ?? []
    }

    private get allIssueFiles(): any[] {
        return [...this.issueReport.images, ...this.issueReport.videos, ...this.issueReport.documents]
    }

    protected override onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        if (this.selectedReportType === EquipmentReportType.Issue && !this.issueReport.systemAffected?.trim()) {
            this.services.notification.warning('System Affected is required.')
            rejectSave('System Affected is required.')
            return
        }

        if (this.selectedReportType === EquipmentReportType.Tire) {
            if (!this.tireReport.problemType?.trim()) {
                this.services.notification.warning('Problem type is required.')
                rejectSave('Problem type is required.')
                return
            }
            if (!this.tireReport.submitterPhone?.trim()) {
                this.services.notification.warning('Your phone number is required.')
                rejectSave('Phone is required.')
                return
            }
        }

        const payload = this.buildPayload()
        if (!payload) {
            rejectSave('No report type selected')
            return
        }

        const typeName = this.getApiTypeName()
        const isEdit = !!payload.id
        const http = this.services.data.http
        const url = `api/EquipmentReport/${typeName}`

        const request = isEdit ? http.put<any>(url, payload) : http.post<any>(url, payload)

        request.subscribe({
            next: (successResult: any) => {
                const tireFiles = this.selectedReportType === EquipmentReportType.Tire ? this.tireReport.images : []
                const tireReceiptFiles =
                    this.selectedReportType === EquipmentReportType.Tire ? this.tireReport.receiptImages : []
                if (this.selectedReportType === EquipmentReportType.Issue && this.allIssueFiles.length === 0) {
                    resolveSave(successResult)
                    return
                }
                if (
                    this.selectedReportType === EquipmentReportType.Tire &&
                    tireFiles.length === 0 &&
                    tireReceiptFiles.length === 0
                ) {
                    resolveSave(successResult)
                    return
                }

                if (this.selectedReportType === EquipmentReportType.Tire) {
                    this.uploadTireFiles(successResult.id, tireFiles, tireReceiptFiles, resolveSave, rejectSave)
                    return
                }

                const uploadData = new FormData()
                uploadData.append('id', successResult.id)
                const files = this.allIssueFiles
                for (let i = 0; i < files.length; i++) {
                    uploadData.append('myFile' + i, files[i], files[i].name)
                }
                this.services.data.post('api/EquipmentReport/UploadImages', uploadData).subscribe({
                    next: () => resolveSave(successResult),
                    error: (err: any) => rejectSave(err),
                })
            },
            error: (err: any) => {
                rejectSave(err)
            },
        })
    }

    private uploadTireFiles(
        reportId: string,
        damageFiles: any[],
        receiptFiles: any[],
        resolveSave: (result?: any) => void,
        rejectSave: (reason?: any) => void,
    ) {
        const doUpload = (files: any[], imageKind: string) =>
            new Promise<void>((resolve, reject) => {
                if (!files.length) {
                    resolve()
                    return
                }
                const uploadData = new FormData()
                uploadData.append('id', reportId)
                uploadData.append('imageKind', imageKind)
                for (let i = 0; i < files.length; i++) {
                    uploadData.append('myFile' + i, files[i], files[i].name)
                }
                this.services.data.post('api/EquipmentReport/UploadTireImages', uploadData).subscribe({
                    next: () => resolve(),
                    error: (err: any) => reject(err),
                })
            })

        doUpload(damageFiles, 'Damage')
            .then(() => doUpload(receiptFiles, 'Receipt'))
            .then(() => resolveSave())
            .catch((err: any) => rejectSave(err))
    }

    private getApiTypeName(): string {
        switch (this.selectedReportType) {
            case EquipmentReportType.Daily:
                return 'Daily'
            case EquipmentReportType.Issue:
                return 'Issue'
            case EquipmentReportType.Fuel:
                return 'Fuel'
            case EquipmentReportType.Grease:
                return 'Grease'
            case EquipmentReportType.Mechanic:
                return 'Mechanic'
            case EquipmentReportType.Tire:
                return 'Tire'
            default:
                return ''
        }
    }

    private buildPayload(): any {
        switch (this.selectedReportType) {
            case EquipmentReportType.Daily:
                return this.buildDailyPayload()
            case EquipmentReportType.Issue:
                return this.buildIssuePayload()
            case EquipmentReportType.Fuel:
                return this.buildFuelPayload()
            case EquipmentReportType.Grease:
                return this.buildGreasePayload()
            case EquipmentReportType.Mechanic:
                return this.buildMechanicPayload()
            case EquipmentReportType.Tire:
                return this.buildTirePayload()
            default:
                return null
        }
    }

    private buildDailyPayload() {
        const d = this.dailyReport
        const inspections = [
            ...this.mapInspections(d.preStartWalkAround, 0),
            ...this.mapInspections(d.safetyEquipment, 1),
            ...this.mapInspections(d.engineOperation, 2),
            ...this.mapInspections(d.hydraulicsAttachments, 3),
            ...this.mapInspections(d.workAreaFinalCheck, 4),
        ]
        return {
            ...this.payloadId(d.id),
            reportType: EquipmentReportType.Daily,
            equipment: d.equipmentId,
            job: d.jobName,
            jobNumber: d.jobNumber,
            operatorHrRef: d.operatorName || null,
            dateTime: d.dateTime,
            issueDetails: d.issueDetails,
            equipmentStatus: d.equipmentStatus,
            inspections,
        }
    }

    private mapInspections(items: InspectionItem[], category: number) {
        return items.map(i => ({
            category,
            label: i.label,
            status: i.status,
        }))
    }

    private buildIssuePayload() {
        const r = this.issueReport
        return {
            ...this.payloadId(r.id),
            reportType: EquipmentReportType.Issue,
            equipment: r.equipmentId,
            job: r.job,
            operatorHrRef: r.operatorName || null,
            dateTime: r.dateTimeOfIssue,
            hoursMileage: r.hoursMileage,
            systemAffected: r.systemAffected,
            failureTypesList: r.failureTypes,
            failureTypeOther: r.failureTypeOther,
            conditionNow: r.conditionNow,
            safetyStatus: r.safetyStatus,
            quickObservationsList: r.quickObservations,
            actionsTakenList: r.actionsTaken,
            severity: r.severity,
            shortDescription: r.shortDescription,
            suggestedMechanicHrRef: r.suggestedMechanicHrRef || null,
        }
    }

    private buildFuelPayload() {
        const f = this.fuelRequest
        return {
            ...this.payloadId(f.id),
            reportType: EquipmentReportType.Fuel,
            equipment: f.equipmentNumber,
            job: f.job,
            operatorHrRef: f.operatorName || null,
            dateTime: f.dateTime,
            currentFuelLevel: f.currentFuelLevel,
            fuelTypeRequired: f.fuelTypeRequired,
            isEquipmentRunning: f.isEquipmentRunning,
            priorityLevel: f.priorityLevel,
            additionalNotes: f.additionalNotes,
        }
    }

    private buildGreasePayload() {
        const g = this.greaseFluidRequest
        return {
            ...this.payloadId(g.id),
            reportType: EquipmentReportType.Grease,
            equipment: g.equipmentNumber,
            job: g.job,
            operatorHrRef: g.operatorName || null,
            dateTime: g.dateTime,
            hoursMileage: g.hoursMileage,
            greaseRoutineLubrication: g.greaseRoutineLubrication,
            greaseDryOrNoisy: g.greaseDryOrNoisy,
            otherFluid: g.otherFluid,
            additionalNotes: g.additionalNotes,
            fluids: g.fluids.map(f => ({
                label: f.label,
                lowOrNeeded: f.lowOrNeeded,
                leakDetected: f.leakDetected,
            })),
            conditionChecks: g.conditionChecks.map(c => ({
                label: c.label,
                checked: c.checked,
            })),
        }
    }

    private buildMechanicPayload() {
        const m = this.mechanicInspection
        return {
            ...this.payloadId(m.id),
            reportType: EquipmentReportType.Mechanic,
            equipment: m.equipmentId,
            job: '',
            operatorHrRef: m.mechanic || null,
            dateTime: m.dateTime,
            workOrderNumber: m.workOrderNumber,
            mechanicHrRef: m.mechanic || null,
            hours: m.hours,
            verifyComplaint: m.verifyComplaint,
            verifyComplaintNotes: m.verifyComplaintNotes,
            visualInspectionFindingsList: m.visualInspectionFindings,
            visualInspectionNotes: m.visualInspectionNotes,
            engineSymptomsList: m.engineSymptoms,
            excessSmokeColor: m.excessSmokeColor,
            engineBasicChecksList: m.engineBasicChecks,
            hydraulicSymptomsList: m.hydraulicSymptoms,
            hydraulicChecksList: m.hydraulicChecks,
            electricalChecksList: m.electricalChecks,
            faultCodes: m.faultCodes,
            drivetrainSymptomsList: m.drivetrainSymptoms,
            brakeFindingsList: m.brakeFindings,
            coolingFindingsList: m.coolingFindings,
            attachmentFindingsList: m.attachmentFindings,
            causeIdentified: m.causeIdentified,
            causeDescription: m.causeDescription,
            status: m.status,
            mechanicNotes: m.mechanicNotes,
            fluidsFilters: m.fluidsFilters.map(f => ({
                label: f.label,
                status: f.status,
            })),
        }
    }

    private payloadId(id?: string): { id?: string } {
        return id ? { id } : {}
    }

    private buildTirePayload() {
        const t = this.tireReport
        return {
            ...this.payloadId(t.id),
            reportType: EquipmentReportType.Tire,
            equipment: t.equipmentId,
            job: t.job,
            jobNumber: t.jobNumber,
            operatorHrRef: t.operatorName || null,
            dateTime: t.dateTimeOfIssue,
            county: t.county,
            equipmentClass: t.equipmentClass,
            problemType: t.problemType,
            axleNumber: t.axleNumber,
            side: t.side,
            resolvedTireSize: t.resolvedTireSize,
            submitterPhone: t.submitterPhone,
            locationDescription: t.locationDescription,
            locationLatitude: t.locationLatitude,
            locationLongitude: t.locationLongitude,
            locationCapturedOn: t.locationCapturedOn,
            shortDescription: t.shortDescription,
            notes: t.notes,
        }
    }
}
